/**
 * When creating a new branch, this function validates that the provided value
 * is a simple, valid git branch name.
 *
 * ⚠️ Note that the validation logic used here is more strict than the
 * specification in https://git-scm.com/docs/git-check-ref-format, so it should
 * only be used during frontend branch creation and NOT to validate branch names
 * that may have originated from git (i.e. bidirection sync).
 *
 * By further restricting names on the frontend to just the most common cases,
 * we avoid the complexity of recreating the full set of rules, guide users
 * towards creating simpler branch names, are able to return more meaningful
 * error messages, while still ensuring the name is git-compatible.
 *
 * @returns For a branch name like `v2.0_branch-name`, this will return `{
 * isValid: true }` if the suffix after the `_` is valid. If invalid, it will
 * return `{ isValid: false, error: 'Branch names cannot contain ".."' }` with
 * an applicable error message.
 *
 */
export function validateSimpleBranchName(branchSuffix: string): { isValid: false; error: string } | { isValid: true } {
  const invalidSubstring = ['/.', '..', '//', '.lock/'].find(char => branchSuffix.includes(char));
  if (invalidSubstring) {
    return {
      isValid: false,
      error: `Branch names cannot contain "${invalidSubstring}".`,
    };
  }

  const invalidEndString = ['/', '.lock', '_'].find(char => branchSuffix.endsWith(char));
  if (invalidEndString) {
    return {
      isValid: false,
      error: `Branch names cannot end with "${invalidEndString}".`,
    };
  }

  // this regex in particular is stricter than the one used in git, where git
  // allows for a larger set of valid characters.
  if (/^[\w.\-/]+$/.test(branchSuffix) === false) {
    return {
      isValid: false,
      error: 'Branch names can only contain alphanumeric, "_", ".", "-", and "/" characters.',
    };
  }

  return { isValid: true };
}
